import React, { useState } from "react";
import { toast } from "react-toastify";
import "../styles/PersonalInfo.css";

const PersonalInfo = ({ onNext, initialData = {} }) => {
  const isContractor =
    (initialData.memberType || "").toLowerCase() === "contractor" ||
    (initialData.role || "").toLowerCase() === "contractor";

  const [profileError, setProfileError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [data, setData] = useState({
    profile: initialData.profile || null,
    firstName: initialData.firstName || "",
    lastName: initialData.lastName || "",
    businessName: initialData.businessName || "",
    dob: initialData.dob || "",
    email: initialData.email || "",
    countryCode: initialData.countryCode || "+61",
    contactNumber: initialData.contactNumber || "",
    street: initialData.street || "",
    cityState: initialData.cityState || "",
    postalCode: initialData.postalCode || "",
    country: initialData.country || "",
    gender: initialData.gender || "",
    taxResidency: initialData.taxResidency || "",
    emergencyContactName: initialData.emergencyContactName || "",
    emergencyContactNumber: initialData.emergencyContactNumber || "",
  });

  const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/gif", "image/webp"];

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;
    if (type === "file" && name === "profile") {
      const file = files[0];
      if (file) {
        if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
          setProfileError("Please upload only image files (JPEG, PNG, GIF, or WebP)");
          setData({ ...data, profile: null });
          e.target.value = "";
          return;
        }
        setProfileError("");
        setData({ ...data, profile: file });
        setFieldErrors((prev) => ({ ...prev, profile: undefined }));
      }
    } else {
      setData({ ...data, [name]: value });
      if (fieldErrors[name]) {
        setFieldErrors((prev) => ({ ...prev, [name]: undefined }));
      }
    }
  };

  const handleNext = () => {
    if (profileError) return;

    const errors = {};
    const trimmed = (v) => String(v ?? "").trim();
    if (!trimmed(data.firstName)) errors.firstName = "First name is required";
    if (!trimmed(data.lastName)) errors.lastName = "Last name is required";
    if (isContractor && !trimmed(data.businessName))
      errors.businessName = "Business name is required";
    if (!trimmed(data.dob)) errors.dob = "Date of birth is required";
    if (!trimmed(data.email)) errors.email = "Email is required";
    if (!trimmed(data.contactNumber))
      errors.contactNumber = "Contact number is required";
    if (!trimmed(data.street)) errors.street = "Street address is required";
    if (!trimmed(data.cityState)) errors.cityState = "City / state is required";
    if (!trimmed(data.postalCode)) errors.postalCode = "Postal code is required";
    if (!trimmed(data.emergencyContactName))
      errors.emergencyContactName = "Emergency contact name is required";
    if (!trimmed(data.emergencyContactNumber))
      errors.emergencyContactNumber = "Emergency contact number is required";

    setFieldErrors(errors);

    if (Object.keys(errors).length > 0) {
      const first = Object.values(errors)[0];
      toast.error(first);
      return;
    }
    onNext(data);
  };

  return (
    <div className="personal-info-container">
      <div className="personal-info-form">
        {/* Profile Upload */}
        <div className="form-row">
          <label>Upload Profile (optional)</label>
          <div className="profile-upload">
            {data.profile && (
              <img
                src={URL.createObjectURL(data.profile)}
                alt="Profile Preview"
                className="profile-preview"
              />
            )}
            {!data.profile && (
              <div className="profile-placeholder">No Image</div>
            )}
            <label className="browse-btn">
              Browse
              <input
                type="file"
                name="profile"
                accept="image/jpeg,image/png,image/gif,image/webp"
                onChange={handleChange}
              />
            </label>
            {profileError && <span className="profile-error">{profileError}</span>}
          </div>
        </div>

        {/* Name Fields */}
        <div className="form-row">
          <label>First Name</label>
          <input
            type="text"
            name="firstName"
            value={data.firstName}
            onChange={handleChange}
            required
          />
        </div>

        <div className="form-row">
          <label>Last Name</label>
          <input
            type="text"
            name="lastName"
            value={data.lastName}
            onChange={handleChange}
            required
          />
        </div>

        {isContractor && (
          <div className="form-row">
            <label>Business Name</label>
            <input
              type="text"
              name="businessName"
              value={data.businessName}
              onChange={handleChange}
              placeholder="Enter business name"
              required
            />
          </div>
        )}

        <div className="form-row">
          <label>DOB</label>
          <input
            type="date"
            name="dob"
            value={data.dob}
            onChange={handleChange}
            required
          />
        </div>

        <div className="form-row">
          <label>Email Address</label>
          <input
            type="email"
            name="email"
            value={data.email}
            onChange={handleChange}
            required
          />
        </div>

        {/* Contact Number */}
        <div className="form-row">
          <label>Contact Number</label>
          <div className="contact-number">
            <select
              name="countryCode"
              value={data.countryCode}
              onChange={handleChange}
            required
            >
              <option value="+61">+61</option>
            </select>
            <input
              type="text"
              name="contactNumber"
              value={data.contactNumber}
              onChange={handleChange}
              inputMode="numeric"
              maxLength="10"
              pattern="[0-9]{10}"
              required
            />
          </div>
        </div>

        {/* Address */}
        <div className="form-row">
          <label>Address</label>
          <span className="address-fields">
            <input
              type="text"
              name="street"
              placeholder="Street Address"
              value={data.street}
              onChange={handleChange}
              required
            />
            <input
              type="text"
              name="cityState"
              placeholder="City / State"
              value={data.cityState}
              onChange={handleChange}
              required

            />
            <input
              type="text"
              name="postalCode"
              placeholder="Postal Code"
              value={data.postalCode}
              onChange={handleChange}
              required
            />
          </span>
        </div>

        {/* Gender */}
        <div className="form-row">
          <label>Gender</label>
          <div className="gender-options">
            <label>
              <input
                type="radio"
                name="gender"
                value="Male"
                onChange={handleChange}
                
              />{" "}
              Male
            </label>
            <label>
              <input
                type="radio"
                name="gender"
                value="Female"
                onChange={handleChange}
              />{" "}
              Female
            </label>
            <label>
              <input
                type="radio"
                name="gender"
                value="Other"
                onChange={handleChange}
              />{" "}
              Other
            </label>
          </div>
        </div>

        {/* Tax Residency - hidden for Contractor */}
        {!isContractor && (
          <div className="tax-residency-section">
            <h3 className="tax-residency-question">Are you: (select only one)</h3>
            <div className="tax-residency-options">
              <label className="tax-residency-option">
                <input
                  type="radio"
                  name="taxResidency"
                  value="australian-resident"
                  checked={data.taxResidency === "australian-resident"}
                  onChange={handleChange}
                />
                <span>An Australian resident for tax purposes</span>
              </label>
              <label className="tax-residency-option">
                <input
                  type="radio"
                  name="taxResidency"
                  value="foreign-resident"
                  checked={data.taxResidency === "foreign-resident"}
                  onChange={handleChange}
                />
                <span>A foreign resident for tax purposes</span>
              </label>
              <label className="tax-residency-option">
                <input
                  type="radio"
                  name="taxResidency"
                  value="working-holiday-maker"
                  checked={data.taxResidency === "working-holiday-maker"}
                  onChange={handleChange}
                />
                <span>A working holiday maker</span>
              </label>
            </div>
          </div>
        )}

        {/* Emergency Contact Person */}
        <div className="form-row">
          <label>Emergency Contact Person</label>
          <div className="emergency-contact-row">
            <input
              type="text"
              name="emergencyContactName"
              value={data.emergencyContactName}
              onChange={handleChange}
              placeholder="Name"
              required
            />
            <input
              type="text"
              name="emergencyContactNumber"
              value={data.emergencyContactNumber}
              onChange={handleChange}
              inputMode="numeric"
              maxLength="10"
              pattern="[0-9]{10}"
              required
            />
          </div>
        </div>

        <div className="form-footer">
          <button className="next-btn" onClick={handleNext}>
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default PersonalInfo;
